'use client';
// DEPLOY: 2026-08-19 — login calls edgeFunctionUrl('auth-login'), NOT /api/auth/login

import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useRouter, useSearchParams } from 'next/navigation';
import AppLogo from '@/components/ui/AppLogo';
import PasswordInput from './PasswordInput';
import { edgeFunctionUrl } from '@/lib/supabase/edge-functions';
import { createClient } from '@/lib/supabase/client';

interface LoginFormData {
  email: string;
  password: string;
}

export default function EmployeeLoginPage() {
  const router = useRouter();
  const searchParams = useSearchParams();
  const nextPath = searchParams.get('next') ?? '/me';
  const urlError = searchParams.get('error');

  const [isLoading, setIsLoading] = useState(false);
  const [authError, setAuthError] = useState<string | null>(
    urlError === 'unauthorized' ? 'You do not have permission to access that page.' : null
  );
  const [loginSuccess, setLoginSuccess] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginFormData>({
    defaultValues: { email: '', password: '' },
  });

  const onSubmit = async (data: LoginFormData) => {
    setIsLoading(true);
    setAuthError(null);

    try {
      // Call the Supabase Edge Function directly — bypasses the Netlify/Next.js server layer entirely.
      // The Edge Function calls signInWithPassword() using the anon key and checks employee_profiles.
      const res = await fetch(edgeFunctionUrl('auth-login'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: data.email, password: data.password }),
      });

      const result = await res.json();

      if (!res.ok) {
        setAuthError(result.error ?? 'Invalid email or password. Please check your credentials and try again.');
        return;
      }

      // Establish the browser session using the tokens returned by the Edge Function.
      // This sets the Supabase auth cookies/localStorage so the rest of the app is authenticated.
      if (result.access_token && result.refresh_token) {
        const supabase = createClient();
        const { error: sessionError } = await supabase.auth.setSession({
          access_token: result.access_token,
          refresh_token: result.refresh_token,
        });

        if (sessionError) {
          console.error('[login] Failed to set browser session:', sessionError.message);
          setAuthError('Sign-in succeeded but session could not be established. Please try again.');
          return;
        }
      }

      setLoginSuccess(true);
      // Small delay to show success state, then redirect
      setTimeout(() => {
        router.push(nextPath);
        router.refresh();
      }, 800);
    } catch {
      setAuthError('Network error. Please check your connection and try again.');
    } finally {
      setIsLoading(false);
    }
  };

  if (loginSuccess) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: 'linear-gradient(160deg, #f0f5ff 0%, #fafcff 100%)' }}>
        <div className="text-center">
          <div className="w-16 h-16 rounded-full bg-primary flex items-center justify-center mx-auto mb-4">
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5">
              <polyline points="20 6 9 17 4 12"/>
            </svg>
          </div>
          <p className="font-bold text-xl text-foreground">Signed in successfully</p>
          <p className="text-muted-foreground text-sm mt-1">Redirecting...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex" style={{ background: 'linear-gradient(160deg, #f0f5ff 0%, #fafcff 60%, #f5f8ff 100%)' }}>
      {/* Left brand panel — hidden on small screens */}
      <div className="hidden lg:flex lg:w-5/12 xl:w-1/2 flex-col items-center justify-center relative overflow-hidden"
        style={{ background: 'linear-gradient(160deg, #0a1a3d 0%, #1a4fa0 60%, #2563eb 100%)' }}>
        <div className="absolute inset-0 opacity-10"
          style={{
            backgroundImage: 'repeating-linear-gradient(45deg, rgba(255,255,255,0.1) 0px, rgba(255,255,255,0.1) 1px, transparent 1px, transparent 50px)',
          }}
        />
        <div className="relative z-10 flex flex-col items-center text-center px-12 max-w-md">
          <div className="w-20 h-20 rounded-2xl bg-white bg-opacity-15 flex items-center justify-center mb-8 border border-white border-opacity-20">
            <AppLogo size={44} className="brightness-200" />
          </div>
          <h1 className="text-4xl font-bold text-white mb-3 tracking-tight">The English Island</h1>
          <p className="text-blue-200 text-lg font-medium mb-8">Staff &amp; Faculty Time Clock</p>
          <div className="flex flex-col gap-4 w-full">
            {[
              { icon: '🔒', title: 'Secure authentication', desc: 'Your account is tied to you personally — no shared PINs' },
              { icon: '⚡', title: 'Fast daily clock-in', desc: 'Scan QR → tap Clock In — done in seconds' },
              { icon: '🕐', title: 'Server-authoritative time', desc: 'Official punches use server time, not your phone clock' },
            ].map(item => (
              <div key={`feature-${item.icon}`} className="flex items-start gap-3 text-left bg-white bg-opacity-10 rounded-xl px-4 py-3 border border-white border-opacity-10">
                <span className="text-xl flex-shrink-0 mt-0.5">{item.icon}</span>
                <div>
                  <p className="text-white font-semibold text-sm">{item.title}</p>
                  <p className="text-blue-200 text-xs mt-0.5 leading-relaxed">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
          <p className="text-blue-300 text-xs mt-8 opacity-70">Staff &amp; faculty accounts only · clock.theenglishisland.net</p>
        </div>
      </div>

      {/* Right login form */}
      <div className="flex-1 flex flex-col items-center justify-center px-6 py-10">
        {/* Mobile logo */}
        <div className="flex lg:hidden items-center gap-3 mb-8">
          <div className="w-10 h-10 rounded-xl bg-primary flex items-center justify-center">
            <AppLogo size={24} className="brightness-200" />
          </div>
          <div>
            <p className="font-bold text-foreground text-base leading-tight">The English Island</p>
            <p className="text-muted-foreground text-xs">Staff &amp; Faculty Time Clock</p>
          </div>
        </div>

        <div className="w-full max-w-sm">
          <div className="mb-7">
            <h2 className="text-2xl font-bold text-foreground mb-1">Sign in to your account</h2>
            <p className="text-muted-foreground text-sm">Use your TEI Staff &amp; Faculty Time Clock credentials to continue.</p>
          </div>

          {/* Staff-only notice */}
          <div className="flex items-start gap-2.5 bg-blue-50 border border-blue-200 rounded-xl px-4 py-3 mb-6">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#1d4ed8" strokeWidth="2" className="flex-shrink-0 mt-0.5">
              <circle cx="12" cy="12" r="10"/>
              <line x1="12" y1="8" x2="12" y2="12"/>
              <line x1="12" y1="16" x2="12.01" y2="16"/>
            </svg>
            <p className="text-blue-800 text-xs leading-relaxed">
              <strong>Staff &amp; faculty accounts only.</strong> This application is exclusively for authorized The English Island employees. Student accounts are not supported here.
            </p>
          </div>

          {/* Auth error */}
          {authError && (
            <div className="flex items-start gap-2.5 bg-red-50 border border-red-200 rounded-xl px-4 py-3 mb-5 slide-up">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#dc2626" strokeWidth="2" className="flex-shrink-0 mt-0.5">
                <circle cx="12" cy="12" r="10"/>
                <line x1="15" y1="9" x2="9" y2="15"/>
                <line x1="9" y1="9" x2="15" y2="15"/>
              </svg>
              <p className="text-red-700 text-sm leading-relaxed">{authError}</p>
            </div>
          )}

          {/* Login form */}
          <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-5" noValidate>
            <div>
              <label htmlFor="email" className="block text-sm font-semibold text-foreground mb-1.5">
                Work email address
              </label>
              <input
                id="email"
                type="email"
                autoComplete="email"
                placeholder="yourname@theenglishisland.net"
                className="input-field"
                {...register('email', {
                  required: 'Email address is required',
                  pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: 'Enter a valid email address' },
                })}
              />
              {errors.email && (
                <p className="text-red-600 text-xs mt-1.5 flex items-center gap-1">
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
                  </svg>
                  {errors.email.message}
                </p>
              )}
            </div>

            <div>
              <div className="flex items-center justify-between mb-1.5">
                <label htmlFor="password" className="block text-sm font-semibold text-foreground">Password</label>
              </div>
              <PasswordInput
                id="password"
                autoComplete="current-password"
                placeholder="Your TEI Staff &amp; Faculty Time Clock password"
                registration={register('password', {
                  required: 'Password is required',
                  minLength: { value: 8, message: 'Password must be at least 8 characters' },
                })}
              />
              {errors.password && (
                <p className="text-red-600 text-xs mt-1.5 flex items-center gap-1">
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
                  </svg>
                  {errors.password.message}
                </p>
              )}
            </div>

            <button
              type="submit"
              disabled={isLoading}
              className={`w-full py-4 rounded-xl font-bold text-white text-base transition-all btn-clock-in ${
                isLoading ? 'opacity-70 cursor-not-allowed' : ''
              }`}
            >
              {isLoading ? (
                <span className="flex items-center justify-center gap-2">
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Signing in...
                </span>
              ) : (
                'Sign In'
              )}
            </button>
          </form>

          <p className="text-center text-xs text-muted-foreground mt-6">
            Contact your administrator if you need account access.
          </p>
        </div>
      </div>
    </div>
  );
}